import { Plugin } from '../../core/plugin-interface.js';
// Font metrics interface removed - not used
/**
 * CLSPreventionPlugin - Prevents Cumulative Layout Shift
 * Focuses on reserving space for dynamic content and optimizing font loading
 */
export class CLSPreventionPlugin extends Plugin {
    constructor(config) {
        super({
            ...config
        });
        this.injectedStyle = null;
        this.name = 'CLSPreventionPlugin';
        this.config = { ...config };
    }
    /**
     * Initialize the plugin
     */
    async init() {
        const startTime = performance.now();
        try {
            this.log('CLS Prevention plugin initialized', 'debug');
        }
        finally {
            this.metrics.initTime = performance.now() - startTime;
        }
    }
    /**
     * Execute CLS prevention by injecting comprehensive CSS rules
     */
    async execute() {
        const startTime = performance.now();
        try {
            this.injectComprehensiveCSS();
            this.setupInteractionHandler();
            this.log('Injected comprehensive CLS prevention stylesheet', 'debug');
        }
        finally {
            this.metrics.executeTime = performance.now() - startTime;
        }
    }
    /**
     * Inject single comprehensive CSS stylesheet covering all CLS scenarios
     */
    injectComprehensiveCSS() {
        const css = `
/* === COMPREHENSIVE CLS PREVENTION STYLESHEET === */

/* === FONT FALLBACK METRICS === */
@font-face {
  font-family: "Roboto-fallback";
  src: local("system-ui");
  size-adjust: 100.3%;
  ascent-override: 92.7%;
  descent-override: 24.4%;
}
@font-face {
  font-family: "Open Sans-fallback";
  src: local("system-ui");
  size-adjust: 104.5%;
  ascent-override: 106.8%;
  descent-override: 29.3%;
}
@font-face {
  font-family: "Lato-fallback";
  src: local("system-ui");
  size-adjust: 97%;
  ascent-override: 108%;
  descent-override: 21%;
}
@font-face {
  font-family: "Montserrat-fallback";
  src: local("system-ui");
  size-adjust: 109%;
  ascent-override: 96.8%;
  descent-override: 27.2%;
}
@font-face {
  font-family: "Inter-fallback";
  src: local("system-ui");
  size-adjust: 105.5%;
  ascent-override: 90%;
  descent-override: 22.5%;
}
@font-face {
  font-family: "Poppins-fallback";
  src: local("system-ui");
  size-adjust: 103%;
  ascent-override: 95%;
  descent-override: 25%;
}
@font-face {
  font-family: "Source Sans Pro-fallback";
  src: local("system-ui");
  size-adjust: 102%;
  ascent-override: 94%;
  descent-override: 25%;
}
@font-face {
  font-family: "Nunito-fallback";
  src: local("system-ui");
  size-adjust: 98%;
  ascent-override: 99%;
  descent-override: 26%;
}
@font-face {
  font-family: "Raleway-fallback";
  src: local("system-ui");
  size-adjust: 101%;
  ascent-override: 93%;
  descent-override: 24%;
}
@font-face {
  font-family: "Playfair Display-fallback";
  src: local("serif");
  size-adjust: 95%;
  ascent-override: 104%;
  descent-override: 28%;
}

/* === ANIMATION CLS PREVENTION === */
[class*="slide"], [class*="fade"], [class*="animate"], [class*="motion"] {
  will-change: transform, opacity !important;
}
.animate-spin, .animate-bounce, .animate-pulse {
  will-change: transform !important;
}
*, *::before, *::after {
  transition-behavior: allow-discrete;
  animation-fill-mode: both;
}

/* === INTERACTIVE ELEMENT STATES === */
button, [role="button"], input[type="submit"], input[type="button"] {
  min-width: var(--cls-width, auto);
  min-height: var(--cls-height, auto);
}

/* === THIRD-PARTY EMBEDS === */
iframe[src*="youtube.com"], iframe[src*="youtu.be"], iframe[src*="vimeo.com"] {
  aspect-ratio: 16 / 9;
}
iframe[src*="twitter.com"], iframe[src*="x.com"] {
  min-height: 250px;
  max-height: 520px;
}
iframe[src*="maps.google.com"], iframe[src*="google.com/maps"] {
  aspect-ratio: 4 / 3;
  min-height: 300px;
}
iframe[src*="instagram.com"] {
  aspect-ratio: 1 / 1;
  max-width: 540px;
}
iframe[src*="googlesyndication.com"], iframe[src*="doubleclick.net"] {
  min-height: 250px;
}

/* === AD SLOT SIZES === */
.ad-container, [class*="ad-"], [id*="ad-"] {
  min-height: 250px;
}
[width="728"], [data-width="728"] {
  min-height: 90px;
}
[width="300"], [data-width="300"] {
  min-height: 250px;
}
[width="336"], [data-width="336"] {
  min-height: 280px;
}

/* === SAFE DEFAULTS === */
[data-src]:not([src]) {
  display: inline-block;
  min-width: 1px;
  min-height: 1px;
  background: #f0f0f0;
}
input, textarea, select {
  font-size: max(16px, 1em);
  box-sizing: border-box;
}
img, video, iframe, embed, object {
  max-width: 100%;
  vertical-align: middle;
}
[data-dynamic-height], .lazy-content {
  transition: min-height 0.3s ease-out;
}
`;
        this.injectedStyle = document.createElement('style');
        this.injectedStyle.id = 'cls-prevention-comprehensive';
        this.injectedStyle.textContent = css;
        // Insert at very beginning of head for maximum priority
        document.head.insertBefore(this.injectedStyle, document.head.firstChild);
    }
    /**
     * Set up interaction handler for dimension preservation
     */
    setupInteractionHandler() {
        document.addEventListener('click', this.handleInteraction.bind(this), true);
    }
    /**
     * Handle user interactions to prevent CLS by preserving element dimensions
     */
    handleInteraction(event) {
        const target = event.target;
        if (target.matches('button, a, [role="button"], input[type="submit"], input[type="button"]')) {
            // Capture original dimensions to prevent layout shift
            const rect = target.getBoundingClientRect();
            target.style.setProperty('--cls-width', `${rect.width}px`);
            target.style.setProperty('--cls-height', `${rect.height}px`);
            // Remove dimension constraints after 500ms grace period
            setTimeout(() => {
                target.style.removeProperty('--cls-width');
                target.style.removeProperty('--cls-height');
            }, 500);
        }
    }
    /**
     * Clean up plugin resources
     */
    async cleanup() {
        await super.cleanup();
        // Remove the single injected stylesheet
        if (this.injectedStyle && this.injectedStyle.parentNode) {
            this.injectedStyle.parentNode.removeChild(this.injectedStyle);
            this.injectedStyle = null;
        }
        // Remove click handler
        document.removeEventListener('click', this.handleInteraction.bind(this), true);
    }
}
//# sourceMappingURL=index.js.map